import React, { useMemo, useRef } from 'react';
import clsx from 'clsx';
import useCenterlineAiStyles from './styles';
import { CitationTargets, MdBlock, MdInline, parseMarkdown } from './core/markdown';
import { citationByNumber, citeTargets, isInspectable } from './core/citations';
import { BotState } from './core/chat-reducer';
import { CitationInfo } from './core/types';
import { CITE_MARKER_ATTR, useCitationCard } from './CitationCard';
import { useCenterlineAi } from './CenterlineAiProvider';

/**
 * An answer's text (chat-ui-spec §5.9 and §6). The markdown tree becomes React elements, so model
 * text is never parsed as HTML. Citation markers are live only in a settled answer; while
 * streaming they stay plain text.
 */

interface MarkdownProps {
  blocks: MdBlock[];
  targets?: CitationTargets;
  citations?: Partial<CitationInfo>[];
}

function CitationMarker(props: {
  n: number;
  href: string | null | undefined;
  info: Partial<CitationInfo> | undefined;
}) {
  const { n, href, info } = props;
  const classes = useCenterlineAiStyles();
  const card = useCitationCard();
  const ref = useRef<HTMLButtonElement>(null);
  if (href) {
    return (
      <a
        className={classes.cite}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title="Open the source in CENTERLINE"
      >
        {n}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      className={classes.citeInfo}
      title="What this citation is"
      {...{ [CITE_MARKER_ATTR]: '' }}
      aria-expanded={!!ref.current && card.openFor === ref.current}
      onClick={(e) => {
        if (info && isInspectable(info)) card.toggle(e.currentTarget, n, info);
      }}
    >
      {n}
    </button>
  );
}

function Inline(props: { nodes: MdInline[] } & Omit<MarkdownProps, 'blocks'>) {
  const { nodes, targets, citations } = props;
  return (
    <>
      {nodes.map((node, index) => {
        const key = index;
        switch (node.type) {
          case 'text':
            return <React.Fragment key={key}>{node.text}</React.Fragment>;
          case 'code':
            return <code key={key}>{node.text}</code>;
          case 'strong':
            return (
              <strong key={key}>
                <Inline nodes={node.children} targets={targets} citations={citations} />
              </strong>
            );
          case 'em':
            return (
              <em key={key}>
                <Inline nodes={node.children} targets={targets} citations={citations} />
              </em>
            );
          case 'link':
            return (
              <a key={key} href={node.href} target="_blank" rel="noopener noreferrer">
                <Inline nodes={node.children} targets={targets} citations={citations} />
              </a>
            );
          case 'cite':
            return (
              <React.Fragment key={key}>
                [
                {node.numbers.map((n, k) => (
                  <React.Fragment key={n}>
                    {k > 0 ? ', ' : null}
                    <CitationMarker
                      n={n}
                      href={targets?.get(n)}
                      info={citationByNumber(citations, n)}
                    />
                  </React.Fragment>
                ))}
                ]
              </React.Fragment>
            );
          default:
            return null;
        }
      })}
    </>
  );
}

export function Markdown(props: MarkdownProps) {
  const { blocks, targets, citations } = props;
  const classes = useCenterlineAiStyles();
  const inline = (nodes: MdInline[]) => (
    <Inline nodes={nodes} targets={targets} citations={citations} />
  );
  return (
    <>
      {blocks.map((block, index) => {
        const key = index;
        switch (block.type) {
          case 'paragraph':
            return <p key={key}>{inline(block.children)}</p>;
          case 'heading': {
            const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3';
            return <Tag key={key}>{inline(block.children)}</Tag>;
          }
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={key} start={block.ordered ? block.start : undefined}>
                {block.items.map((item, k) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={k}>{inline(item)}</li>
                ))}
              </Tag>
            );
          }
          case 'code':
            return (
              <pre key={key}>
                <code>{block.text}</code>
              </pre>
            );
          case 'table':
            return (
              <div key={key} className={classes.tableWrap} tabIndex={0} role="region">
                <table>
                  <thead>
                    <tr>
                      {block.header.map((cell, k) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <th
                          key={k}
                          style={block.align[k] ? { textAlign: block.align[k]! } : undefined}
                        >
                          {inline(cell)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={r}>
                        {row.map((cell, k) => (
                          <td
                            // eslint-disable-next-line react/no-array-index-key
                            key={k}
                            style={block.align[k] ? { textAlign: block.align[k]! } : undefined}
                          >
                            {inline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </>
  );
}

/** The answer area of a bot turn in each of its states. */
export default function Answer(props: { bot: BotState; projectId: string | null }) {
  const { bot, projectId } = props;
  const classes = useCenterlineAiStyles();
  const { slugs } = useCenterlineAi();

  const settled = bot.status === 'done';
  const source =
    bot.status === 'streaming' ? bot.streamedText : bot.answer !== undefined ? bot.answer : '';
  const targets = useMemo(
    () => (settled ? citeTargets(bot.citations, slugs, projectId) : undefined),
    [settled, bot.citations, slugs, projectId],
  );
  const blocks = useMemo(() => parseMarkdown(source, targets), [source, targets]);

  if (bot.status === 'pending') {
    return <div className={clsx(classes.answer, classes.answerPending)}>Thinking…</div>;
  }
  if (bot.status === 'error') {
    return <div className={clsx(classes.answer, classes.answerError)}>{bot.message}</div>;
  }
  if (bot.status === 'stopped' && bot.answer === undefined) {
    return <div className={clsx(classes.answer, classes.answerPending)}>{bot.message}</div>;
  }
  return (
    <div className={clsx(classes.answer, bot.status === 'streaming' && classes.answerStreaming)}>
      <Markdown blocks={blocks} targets={targets} citations={bot.citations} />
    </div>
  );
}
